/**
 * Pitch Deck Studio — Valentine
 * Chat permanent cross-workspace, génère PDF / Slides HTML / PPTX à la demande.
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Send, Mic, MicOff, FileText, Presentation as PresentIcon, FileArchive, Download, Plus, Sparkles, Volume2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const SUGGESTED_SECTIONS = {
  'Pitch investisseur (10 slides)': ['Problème', 'Solution', 'Marché', 'Business Model', 'Traction', 'Concurrence', 'Équipe', 'Roadmap', 'Besoin de financement', 'Contact'],
  'Dossier commercial complet': ['Contexte Bureau Agricole', 'Les 7 différenciateurs', '13 métiers ciblés', 'Projections 5 ans', 'Pourquoi maintenant', 'Prochaine étape'],
  'Argumentaire associé commercial': ['Qui je suis', 'Le produit', 'Pourquoi toi', 'Ce que je te propose', 'Les étapes', 'Prochains pas'],
  'Vide (je compose)': [],
};

export default function PitchDeckValentinePage() {
  const [thread, setThread] = useState(null);
  const [threads, setThreads] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recorder, setRecorder] = useState(null);
  const [continuousMode, setContinuousMode] = useState(true);  // Mode tracteur : auto-send + auto-TTS
  const [autoTTS, setAutoTTS] = useState(true);
  const [ingestMeta, setIngestMeta] = useState(null);  // {emails_detectes, campagnes_dispo}
  const [lastPastedText, setLastPastedText] = useState('');
  const [campagnes, setCampagnes] = useState([]);
  const [showGenDialog, setShowGenDialog] = useState(false);
  const [genType, setGenType] = useState('pdf');
  const [genTitre, setGenTitre] = useState('');
  const [genSections, setGenSections] = useState([]);
  const [genInstructions, setGenInstructions] = useState('');
  const [preset, setPreset] = useState('Dossier commercial complet');
  const [generating, setGenerating] = useState(false);
  const scrollRef = useRef(null);
  const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;

  const loadThread = async (tid = null) => {
    try {
      const url = tid ? `/pitch-deck/thread?thread_id=${tid}` : '/pitch-deck/thread';
      const r = await api.get(url);
      setThread(r.data);
    } catch (e) {
      toast.error('Chargement thread impossible');
    }
  };

  const loadThreadsList = async () => {
    try {
      const r = await api.get('/pitch-deck/threads');
      setThreads(r.data.threads || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadThread();
    loadThreadsList();
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread?.messages?.length]);

  const speak = (text) => {
    if (!synth || !autoTTS) return;
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'fr-FR';
    u.rate = 1.05;
    // Voix FR préférée (meilleur rendu mobile)
    const voices = synth.getVoices();
    const frVoice = voices.find(v => v.lang.startsWith('fr'));
    if (frVoice) u.voice = frVoice;
    synth.speak(u);
  };

  const send = async (overrideText = null) => {
    const text = overrideText || input;
    if (!text || !text.trim()) return;
    setSending(true);
    try {
      const r = await api.post('/pitch-deck/chat', {
        message: text,
        thread_id: thread?.id,
      });
      // Si une liste d'emails a été détectée, prépare l'UI d'ingest
      if (r.data.ingest_meta && r.data.ingest_meta.emails_detectes >= 3) {
        setIngestMeta(r.data.ingest_meta);
        setLastPastedText(text);
        // Charger les campagnes dispo pour le dropdown
        try {
          const cl = await api.get('/pitch-deck/campagnes-dispo');
          setCampagnes(cl.data?.campagnes || []);
        } catch { /* ignore */ }
        toast.success(`${r.data.ingest_meta.emails_detectes} emails détectés — choisis une campagne`);
      }
      setInput('');
      await loadThread(thread?.id);
      if (r.data.reply) speak(r.data.reply);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Valentine n\'a pas répondu');
    } finally {
      setSending(false);
    }
  };

  const confirmIngest = async (campagneId) => {
    if (!campagneId || !lastPastedText) return;
    try {
      const r = await api.post('/pitch-deck/ingest-from-chat', {
        thread_id: thread?.id,
        raw_text: lastPastedText,
        campagne_id: campagneId,
        source: 'pitch-deck-chat',
      });
      toast.success(`${r.data.ingested} prospects injectés`);
      setIngestMeta(null);
      setLastPastedText('');
      await loadThread(thread?.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ingest échoué');
    }
  };

  const startRecording = async () => {
    try {
      // Couper le TTS en cours pour laisser parler Fred
      if (synth) synth.cancel();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = new MediaRecorder(stream);
      const chunks = [];
      r.ondataavailable = (e) => chunks.push(e.data);
      r.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const fd = new FormData();
        fd.append('file', blob, 'input.webm');
        try {
          const t = await api.post('/journee-chauffeur/transcrire', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          const text = t.data.text;
          if (text) {
            if (continuousMode) {
              // Mode tracteur : auto-send direct
              setInput('');
              await send(text);
            } else {
              setInput(prev => prev ? `${prev} ${text}` : text);
            }
          }
        } catch { toast.error('Transcription impossible'); }
      };
      r.start();
      setRecorder(r);
      setRecording(true);
    } catch { toast.error('Micro refusé — autorise le micro dans les paramètres'); }
  };

  const stopRecording = () => {
    if (recorder) {
      recorder.stop();
      setRecording(false);
      setRecorder(null);
    }
  };

  const newThread = async () => {
    const titre = window.prompt('Nom du nouveau pitch :', 'Pitch Deck ' + new Date().toLocaleDateString('fr-FR'));
    if (!titre) return;
    try {
      const r = await api.post(`/pitch-deck/thread/new?titre=${encodeURIComponent(titre)}`);
      setThread(r.data);
      await loadThreadsList();
      toast.success('Nouveau pitch créé');
    } catch { toast.error('Création impossible'); }
  };

  const openGenerate = () => {
    setGenTitre(thread?.titre || 'Pitch Deck Bureau Agricole');
    setGenSections(SUGGESTED_SECTIONS['Dossier commercial complet']);
    setPreset('Dossier commercial complet');
    setShowGenDialog(true);
  };

  const applyPreset = (p) => {
    setPreset(p);
    setGenSections(SUGGESTED_SECTIONS[p] || []);
  };

  const generate = async () => {
    if (!genTitre.trim() || genSections.length === 0) {
      toast.error('Titre et sections obligatoires');
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post('/pitch-deck/generate', {
        thread_id: thread?.id,
        doc_type: genType,
        titre: genTitre,
        sections: genSections,
        instructions: genInstructions || null,
      });
      toast.success('Document généré !');
      setShowGenDialog(false);
      // Télécharger
      const url = r.data.download_url;
      if (url) {
        const a = document.createElement('a');
        a.href = url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
        a.download = r.data.doc.filename;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      await loadThread(thread?.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Génération impossible');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="h-[calc(100dvh-6rem)] flex flex-col md:flex-row gap-4 p-4" data-testid="pitch-deck-page">
      {/* Sidebar threads */}
      <aside className="md:w-64 shrink-0 flex md:flex-col gap-2 overflow-x-auto md:overflow-x-visible md:overflow-y-auto">
        <Button size="sm" onClick={newThread} data-testid="btn-new-thread" className="shrink-0">
          <Plus className="w-4 h-4 mr-1" /> Nouveau pitch
        </Button>
        {threads.map(t => (
          <button
            key={t.id}
            onClick={() => loadThread(t.id)}
            className={`text-left text-xs p-2 rounded border shrink-0 md:shrink ${thread?.id === t.id ? 'bg-emerald-50 border-emerald-500' : 'bg-card hover:bg-muted'}`}
            data-testid={`thread-${t.id}`}
          >
            <div className="font-semibold truncate">{t.titre}</div>
            <div className="text-muted-foreground">{(t.updated_at || '').slice(0, 10)}</div>
          </button>
        ))}
      </aside>

      {/* Chat */}
      <div className="flex-1 flex flex-col min-h-0">
        <Card className="flex-1 flex flex-col min-h-0">
          <CardHeader className="pb-2 border-b flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="w-5 h-5 text-emerald-600" /> {thread?.titre || 'Pitch Deck Studio'}
              <Badge variant="outline" className="text-xs">{thread?.messages?.length || 0} msg</Badge>
            </CardTitle>
            <div className="flex gap-2 items-center">
              <label className="flex items-center gap-1 text-xs cursor-pointer" title="Mode tracteur : auto-envoi après dictée + Valentine lit sa réponse">
                <input
                  type="checkbox"
                  checked={continuousMode}
                  onChange={(e) => setContinuousMode(e.target.checked)}
                  className="w-4 h-4"
                  data-testid="toggle-continuous"
                />
                🚜 Mode tracteur
              </label>
              <label className="flex items-center gap-1 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoTTS}
                  onChange={(e) => setAutoTTS(e.target.checked)}
                  className="w-4 h-4"
                  data-testid="toggle-tts"
                />
                🔊 Voix
              </label>
              <Button size="sm" onClick={openGenerate} data-testid="btn-generate-doc" className="bg-emerald-600 hover:bg-emerald-700">
                <FileArchive className="w-4 h-4 mr-1" /> Générer doc
              </Button>
            </div>
          </CardHeader>
          <CardContent ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {(!thread?.messages || thread.messages.length === 0) && (
              <div className="text-center text-muted-foreground text-sm py-8">
                <p>Commence à écrire ou dicter — Valentine garde l'historique de tout ton pitch deck.</p>
                <p className="mt-2 text-xs">Ex: « Attaque un pitch 10 slides pour un investisseur AgTech »</p>
              </div>
            )}
            {thread?.messages?.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-3 rounded-lg text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-emerald-600 text-white' : 'bg-muted'}`}>
                  {m.content}
                  {m.role === 'assistant' && (
                    <button onClick={() => speak(m.content)} className="ml-2 opacity-60 hover:opacity-100" title="Lire">
                      <Volume2 className="w-3 h-3 inline" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start"><div className="bg-muted p-3 rounded-lg text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Valentine réfléchit…</div></div>
            )}
          </CardContent>
          {/* Panneau Ingest-liste (apparaît quand Valentine détecte 3+ emails) */}
          {ingestMeta && (
            <div className="mx-3 mt-2 mb-0 p-3 bg-emerald-50 border-2 border-emerald-500 rounded-lg" data-testid="ingest-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="font-bold text-emerald-900 text-sm">
                  📋 {ingestMeta.emails_detectes} emails détectés
                </div>
                <button
                  onClick={() => { setIngestMeta(null); setLastPastedText(''); }}
                  className="text-xs text-muted-foreground hover:text-foreground"
                  data-testid="btn-cancel-ingest"
                >
                  Annuler
                </button>
              </div>
              <div className="text-xs text-muted-foreground mb-2">
                Échantillon : {(ingestMeta.echantillon || []).slice(0, 3).join(', ')}
                {ingestMeta.emails_detectes > 3 && ` +${ingestMeta.emails_detectes - 3} autres`}
              </div>
              <div className="text-xs font-semibold mb-1">Injecter dans quelle campagne ?</div>
              <div className="grid grid-cols-1 gap-1 max-h-32 overflow-y-auto">
                {campagnes.map(c => (
                  <button
                    key={c.id}
                    onClick={() => confirmIngest(c.id)}
                    className="text-left text-xs p-2 rounded bg-white border hover:bg-emerald-100 hover:border-emerald-500 transition-colors"
                    data-testid={`ingest-camp-${c.id}`}
                  >
                    <div className="font-semibold truncate">{c.nom}</div>
                    {!c.a_des_variantes && <span className="text-amber-600 text-[10px]">⚠ pas encore de message</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t flex gap-2 items-end">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={continuousMode ? "🚜 Mode tracteur : appuie et parle, ça part direct" : "Dis à Valentine ce que tu veux travailler…"}
              className="min-h-[48px] max-h-32 resize-none"
              rows={2}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              data-testid="pitch-chat-input"
            />
            <Button
              size="lg"
              variant={recording ? 'destructive' : 'default'}
              className={`${recording ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-emerald-600 hover:bg-emerald-700'} h-16 w-16 rounded-full shrink-0`}
              onMouseDown={startRecording}
              onMouseUp={stopRecording}
              onTouchStart={(e) => { e.preventDefault(); startRecording(); }}
              onTouchEnd={(e) => { e.preventDefault(); stopRecording(); }}
              data-testid="btn-mic-pitch"
              title="Maintenir pour parler"
            >
              {recording ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
            </Button>
            <Button onClick={() => send()} disabled={sending || !input.trim()} data-testid="btn-send-pitch" size="icon" className="h-16 w-12 shrink-0">
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </Button>
          </div>
        </Card>

        {/* Docs générés */}
        {(thread?.docs_generes?.length || 0) > 0 && (
          <div className="mt-3 flex gap-2 overflow-x-auto">
            {thread.docs_generes.map(d => (
              <a
                key={d.id}
                href={`${process.env.REACT_APP_BACKEND_URL}/api/pitch-deck/download/${thread.id}/${d.id}`}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 bg-emerald-50 border border-emerald-300 rounded px-3 py-2 text-xs hover:bg-emerald-100 flex items-center gap-2"
                data-testid={`doc-${d.id}`}
              >
                {d.doc_type === 'pdf' ? <FileText className="w-4 h-4" /> : d.doc_type === 'pptx' ? <PresentIcon className="w-4 h-4" /> : <FileArchive className="w-4 h-4" />}
                <div>
                  <div className="font-semibold">{d.titre}</div>
                  <div className="text-muted-foreground">{d.doc_type.toUpperCase()} • {(d.size / 1024).toFixed(0)} Ko</div>
                </div>
                <Download className="w-3 h-3" />
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Dialog génération */}
      <Dialog open={showGenDialog} onOpenChange={setShowGenDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Générer un document</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold">Type</label>
              <div className="flex gap-2 mt-1">
                {['pdf', 'slides_html', 'pptx'].map(t => (
                  <Button key={t} size="sm" variant={genType === t ? 'default' : 'outline'} onClick={() => setGenType(t)} data-testid={`gentype-${t}`}>
                    {t.toUpperCase()}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold">Titre</label>
              <Input value={genTitre} onChange={(e) => setGenTitre(e.target.value)} data-testid="gen-titre" />
            </div>
            <div>
              <label className="text-xs font-semibold">Preset</label>
              <select
                className="w-full border rounded px-2 py-1 text-sm mt-1 bg-background"
                value={preset}
                onChange={(e) => applyPreset(e.target.value)}
                data-testid="gen-preset"
              >
                {Object.keys(SUGGESTED_SECTIONS).map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold">Sections (une par ligne)</label>
              <Textarea
                value={genSections.join('\n')}
                onChange={(e) => setGenSections(e.target.value.split('\n').filter(x => x.trim()))}
                rows={6}
                data-testid="gen-sections"
              />
            </div>
            <div>
              <label className="text-xs font-semibold">Instructions pour Valentine (optionnel)</label>
              <Textarea
                value={genInstructions}
                onChange={(e) => setGenInstructions(e.target.value)}
                rows={2}
                placeholder="Ex : ton confraternel, cible investisseur AgTech France, 300M€ sous management…"
                data-testid="gen-instructions"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenDialog(false)}>Annuler</Button>
            <Button onClick={generate} disabled={generating} data-testid="btn-generate-confirm">
              {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération…</> : <><FileArchive className="w-4 h-4 mr-2" />Générer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
